import StatusChip from 'components/@extended/StatusChip';
import { RequiredAsterisk } from 'components/forms/inputs/rhf-inputs/InputWrapper';
import { FIELD_LABEL_CLASS } from 'components/GridItemList';
import { CheckIcon, ChevronDownIcon } from 'components/icons';
import { SectionEditButton } from 'components/section-edit/SectionEdit';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import { NEW_FOCUS_RING } from 'layout/NewDesignLayout/focusRing';
import { ReactNode } from 'react';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';

export type BlockTone = 'done' | 'missing' | 'plain';

const NUMBER_CLASS: Record<BlockTone, string> = {
    done: 'bg-success-lighter text-fg-success new:bg-success-tint new:text-success-ink',
    missing: 'bg-warning-lighter text-fg-warning new:bg-warning-tint new:text-warning-ink',
    plain: 'bg-grey-100 text-fg-secondary new:bg-neutral-tint new:text-foreground-secondary',
};

export const DOT_CLASS: Record<BlockTone, string> = {
    done: 'bg-success',
    missing: 'bg-warning',
    plain: 'bg-grey-400',
};

export const blockTitleId = (id: string) => `${id}-title`;

export const BLOCK_CARD_CLASS =
    'bg-card border-border new:border-0 new:shadow-card new:outline-hidden scroll-mt-4 rounded-[10px] border';

interface Props {
    id: string;
    number: number;
    title: string;
    summary: ReactNode;
    tone: BlockTone;
    missingCount?: number;
    onEdit?: () => void;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    children: ReactNode;
}

export function Block({ id, number, title, summary, tone, missingCount, onEdit, open, onOpenChange, children }: Props) {
    return (
        <Collapsible
            open={open}
            onOpenChange={onOpenChange}
            id={id}
            role="region"
            aria-labelledby={blockTitleId(id)}
            className={BLOCK_CARD_CLASS}
        >
            <div
                className="flex items-center gap-2.5 px-5 py-3"
                onClick={(event) => {
                    // Edit and the chevron act on their own; the rest of the row toggles like the chevron does.
                    if (!(event.target as HTMLElement).closest('button, a')) onOpenChange(!open);
                }}
            >
                <span
                    aria-hidden
                    className={cn(
                        'text-caption inline-flex size-6 shrink-0 items-center justify-center rounded-full font-semibold',
                        NUMBER_CLASS[tone]
                    )}
                >
                    {number}
                </span>
                <div className="min-w-0 flex-auto">
                    <h3 id={blockTitleId(id)} tabIndex={-1} className="text-section m-0 outline-none">
                        {title}
                    </h3>
                    <div className="text-caption text-muted-foreground">{summary}</div>
                </div>
                {onEdit ? <SectionEditButton onClick={onEdit} /> : null}
                {missingCount ? (
                    <MissingChip label={t('client-identification.n-missing', { count: String(missingCount) })} />
                ) : tone === 'done' ? (
                    <CheckIcon aria-hidden className="text-success size-4 shrink-0" />
                ) : null}
                <CollapsibleTrigger
                    aria-label={t(open ? 'client-identification.hide-block' : 'client-identification.show-block', {
                        title,
                    })}
                    className={cn(
                        'text-foreground-tertiary hover:text-foreground hover:bg-neutral-tint inline-flex size-8 shrink-0 items-center justify-center rounded-full',
                        NEW_FOCUS_RING
                    )}
                >
                    <ChevronDownIcon
                        className={cn(
                            'size-4 motion-safe:transition-transform motion-safe:duration-(--duration-fast)',
                            open && '-scale-y-100'
                        )}
                    />
                </CollapsibleTrigger>
            </div>
            <CollapsibleContent className="border-border flex flex-col gap-4 border-t px-5 py-4">
                {children}
            </CollapsibleContent>
        </Collapsible>
    );
}

export function MissingChip({ label = t('client-identification.missing') }: { label?: string }) {
    return <StatusChip type="warning" size="small" label={label} />;
}

export const VALUE_GRID_CLASS = 'grid grid-cols-1 gap-x-6 gap-y-3 @md:grid-cols-2 @2xl:grid-cols-3 @container';

interface ValueProps {
    label: ReactNode;
    children?: ReactNode;
    description?: ReactNode;
    detail?: ReactNode;
    required?: boolean;
    missing?: boolean;
    wide?: boolean;
    className?: string;
}

export function Value({ label, children, description, detail, required, missing, wide, className }: ValueProps) {
    const isEmpty = children === null || children === undefined || children === '';
    return (
        <div className={cn('flex min-w-0 flex-col gap-0.5', wide && 'col-span-full', className)}>
            <div className={FIELD_LABEL_CLASS}>
                {label}
                {required ? <RequiredAsterisk /> : null}
            </div>
            {description ? <p className="text-caption text-foreground-secondary m-0">{description}</p> : null}
            <div className="text-body1 flex flex-wrap items-center gap-2 break-words">
                <span className="min-w-0">{isEmpty ? '-' : children}</span>
                {missing ? <MissingChip /> : null}
            </div>
            {detail ? <div className="text-caption text-foreground-secondary">{detail}</div> : null}
        </div>
    );
}
